import { useState } from "react";
import { unservedStudents, withAssignStatus, type AssignPlan, type CreateHomeworkRequest } from "@edu/shared";
import * as api from "../../../lib/api.ts";
import { useViewer } from "../../../lib/session.tsx";
import type { AssignOutcomeView } from "./AssignResult.tsx";
import { NETWORK_ROW_TEXT, allFailureText, rowFailureText } from "./assign-errors.ts";
import { runAssignCycle } from "./cycle.ts";
import { dropPlan, savePlan } from "./plan-storage.ts";

/**
 * Ход раздачи: цикл, его прогресс, судьба плана и слова отказов.
 *
 * Отдельным модулем от формы, потому что это разные вопросы: форма отвечает на
 * «что задаём и кому», а здесь живёт «что делать, когда на седьмом ученике
 * пропала связь». Смешанные в одном файле, они дают ровно ту длинную функцию,
 * правка по куску которой ложится в соседний обработчик.
 */

export interface AssignRun {
  plan: AssignPlan | null;
  outcomes: ReadonlyMap<string, AssignOutcomeView>;
  busy: boolean;
  /** Сколько ответов пришло: «Создаём… 5 из 12». */
  progress: number;
  /**
   * Сколько ученикам цикл собирался написать. ⚠️ Это НЕ размер плана: повтор для
   * оставшихся идёт по троим из двенадцати, и «Создаём… 1 из 12» врало бы.
   */
  targets: number;
  /** Отказ, повторяющийся у всех. Показывается один раз вместо панели. */
  allFailure: string | null;
  /** Хранилище не пишет: план живёт в памяти вкладки, и это надо сказать. */
  storageBroken: boolean;
  /** Новое нажатие «Задать»: план уже собран формой. */
  start: (plan: AssignPlan) => void;
  /** Повтор для оставшихся: ТЕ ЖЕ ключи идемпотентности. */
  retry: () => void;
  /** Показать восстановленный план, ничего не отправляя. */
  restore: (plan: AssignPlan) => void;
}

export function useAssignRun(opts?: {
  /**
   * Название темы по слагу для слов отказа. Приходит из справочника формы:
   * отказ `generator_unavailable` несёт слаг, а человек выбирал название.
   */
  topicName?: (skill: string) => string | null;
}): AssignRun {
  const [plan, setPlan] = useState<AssignPlan | null>(null);
  const [outcomes, setOutcomes] = useState<ReadonlyMap<string, AssignOutcomeView>>(new Map());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [targets, setTargets] = useState(0);
  const [allFailure, setAllFailure] = useState<string | null>(null);
  const [storageBroken, setStorageBroken] = useState(false);
  /**
   * ⚠️ В просмотре кабинета админом план не пишется в `localStorage` (12.6
   * `docs/admin-panel-design.md`): в нём идентификаторы учеников, и они
   * остались бы в браузере админа на 30 суток. Сервер работы всё равно не
   * создаст, `view_only`, так что ключам идемпотентности беречь нечего.
   */
  const viewing = useViewer() !== null;
  const save = (p: AssignPlan): void => {
    if (!viewing && !savePlan(p)) setStorageBroken(true);
  };

  const cycle = async (initial: AssignPlan): Promise<void> => {
    const rest = unservedStudents(initial);
    setBusy(true);
    setAllFailure(null);
    setProgress(0);
    setTargets(rest.length);
    let current = initial;
    const startedAt = Date.now();

    const report = await runAssignCycle({
      targets: rest,
      isOnline: () => navigator.onLine !== false,
      create: async (student) => {
        const body: CreateHomeworkRequest = {
          requestId: student.requestId,
          studentUserId: student.userId,
          // Поля нет вовсе, а не `undefined`: домашка без источника заданий
          ...(current.source === undefined ? {} : { source: current.source }),
          ...(current.taskText === undefined ? {} : { taskText: current.taskText }),
          ...(current.materialFileIds === undefined || current.materialFileIds.length === 0
            ? {}
            : { materialFileIds: current.materialFileIds }),
          ...(current.batchId === null ? {} : { batchId: current.batchId }),
          ...(current.title === null ? {} : { title: current.title }),
          ...(current.dueAt === null ? {} : { dueAt: current.dueAt }),
          ...(current.lessonId === null ? {} : { lessonId: current.lessonId }),
        };
        // Сводкой, а не карточкой: на двенадцати работах это около 57 КБ разницы
        return await api.createHomeworkSummary(body);
      },
      onOutcome: (student, outcome) => {
        current = withAssignStatus(
          current,
          student.userId,
          outcome.kind === "created" ? "created" : outcome.kind === "failed" ? "failed" : "pending",
        );
        // Запись синхронная, дебаунс не нужен: это одно событие на круг по сети
        save(current);
        setPlan(current);
        setProgress((n) => n + 1);
        setOutcomes((prev) => {
          const next = new Map(prev);
          if (outcome.kind === "created") {
            next.set(student.userId, { homeworkId: outcome.homeworkId });
          } else if (outcome.kind === "failed") {
            next.set(student.userId, { reason: rowFailureText(outcome.code) });
          } else {
            next.set(student.userId, { reason: NETWORK_ROW_TEXT });
          }
          return next;
        });
      },
    });

    /**
     * План удаляется по признаку «все обслужены», а не по возрасту.
     *
     * ⚠️ Второй случай удаления узкий намеренно: «не создано ни одной работы»
     * недостаточно. Сетевая неудача это «запрос ушёл, ответ не вернулся», то
     * есть работа МОГЛА быть создана, и выброшенный ключ идемпотентности означал
     * бы вторую работу при следующей попытке. Поэтому пустой план удаляется
     * только при отказе «на всех» без единой сетевой неудачи: там сервер
     * гарантированно ничего не записал.
     */
    const created = current.students.filter((s) => s.status === "created").length;
    const settled = created === current.students.length;
    const surelyEmpty = created === 0 && report.networkFailures === 0 && report.stopped === "all";
    if (settled || surelyEmpty) dropPlan(current.planRunId);
    if (report.stopped === "all") {
      setAllFailure(allFailureText(report.allFailure, opts?.topicName));
    }
    setBusy(false);

    /**
     * Прибор под условие пересмотра «цикл дольше десяти секунд, значит нужна
     * серверная пачка» (5.12). Отказ отправки глушится: неудача прибора не
     * должна быть видна тому, кто только что задал домашку двенадцати.
     */
    void api
      .sendHomeworkBatchEvent({
        event: "homework_batch_done",
        // Сколько ученикам цикл писал на самом деле: `durationMs` меряет
        // именно их, и на повторе для троих размер плана врал бы
        students: rest.length,
        durationMs: Date.now() - startedAt,
        networkFailures: report.networkFailures,
        // Источник заказа, а не константа: разрез «plan против oge» это половина
        // смысла замера, потому что домашка по темам собирается генераторами и
        // круг по сети у неё дороже
        source: current.source?.kind ?? "material",
      })
      .catch(() => undefined);
  };

  return {
    plan,
    outcomes,
    busy,
    progress,
    targets,
    allFailure,
    storageBroken,
    start: (fresh) => {
      setOutcomes(new Map());
      setPlan(fresh);
      save(fresh);
      void cycle(fresh);
    },
    retry: () => {
      if (plan !== null && !busy) void cycle(plan);
    },
    restore: (known) => {
      setOutcomes(new Map());
      setAllFailure(null);
      setPlan(known);
    },
  };
}
